import "./AdminAssessmentTable.css";

import { useTranslation } from "react-i18next";
import React, { use, useEffect, useState } from "react";
import {
    createAssessment,
    downloadBlankTemplate,
    getAllAssessments,
    getAssessmentDetails,
    saveAssessment,
} from "../../../services/svt/svt-admin/assessment-management.service";

import { DataTableComponent } from "../../common/TableComponent/DataTableComponent";
import Loader from "../../common/Loader/Loader";
import ModalComponent from "../../common/ModalComponent/ModalComponent";
import TableComponent from "../../common/TableComponent/TableComponent";
import downloadIcon from "../../../assets/DownloadIcon.jpg";
import { formatDate } from "../../../utils/dateFormat";
import { getAllTemplates } from "../../../services/svt/svt-admin/template-management.service";
import { getAllUserLanguages } from "../../../services/user-services/user-level-services";
import { sortByCreatedDateDesc } from "../../../utils/sortByDate";
import { toast } from "react-toastify";


let languageOptions = [
    { label: "English", value: "1" },
    { label: "Japanese", value: "2" },
];

const AdminAssessmentTable = ({
    setActiveTab,
    setAssessmentDetails,
    modalMode,
    setModalMode,
    setIsAddQueModalOpen,
    setAssessmentPayload,
    setTableRowData,
}) => {

    const { t } = useTranslation();
    const [isModalOpen, setModalOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [assessmentData, setAssessmentData] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const { i18n } = useTranslation();


    const columns = [
        { name: t("assessmentName"), field: "assessmentName", selector: (row) => row.assessmentName, sortable: true, width: '200px' },
        { name: t("language"), field: "language", selector: (row) => row.language, sortable: true },
        // { name: t("shortCode"), field: "shortCode", selector: (row) => row.shortCode, sortable: true },
        { name: t("runningVersion"), field: "runningVersion", selector: (row) => row.runningVersion, sortable: true, width: '100px' },
        { name: t("uploadedDate"), field: "uploadedDate", selector: (row) => row?.uploadedDate, sortable: true },
        { name: t("published"), field: "isPublished", selector: (row) => row.isPublished, sortable: true },
        { name: t("slot"), field: "slot", selector: (row) => row.slot, sortable: true },
    ];

    useEffect(() => {
        fetchAssessmentData();
        fetchTemplateData();
        fetchUserLanguages();
    }, [i18n.language]);

   
    const fetchUserLanguages = async () => {
        setLoading(true);
        const lang = await getAllUserLanguages();
        let Options = [];
        if (lang?.data) {
            Options = [
                ...lang.data.map(({ id, name }) => ({
                    value: id,
                    label: name,
                })),
            ];
            languageOptions = Options;
        }
        setLoading(false);
    };

    const fetchAssessmentData = async () => {
        try {
            setLoading(true);
            const res = await getAllAssessments();
            setAssessmentData(res.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssessmentDetails = async (assessmentMasterId, langId) => {
        try {
            setLoading(true);
            const res = await getAssessmentDetails(assessmentMasterId, langId);
            setAssessmentDetails(res.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTemplateData = async () => {
        try {
            setLoading(true);
            const templatesResult = await getAllTemplates();
            setTemplates(
                templatesResult?.data?.filter((item) => item.status === true)
            );
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const getDate = (uploadedDate) => {
        const date = new Date(uploadedDate);
        const options = { day: "2-digit", month: "short", year: "numeric" };
        return date.toLocaleDateString("en-GB", options).replace(/ /g, "-");
    };

    const data = sortByCreatedDateDesc(
        assessmentData.map((item) => {
            let assessmentName = item.assessmentName;
            let langKey = '';
            // Map item.langName to translation key
            if (item.langName && typeof item.langName === 'string') {
                const lower = item.langName.toLowerCase();
                if (lower.includes('jap')) langKey = 'japanese';
                else if (lower.includes('ch')) langKey = 'simplifiedChinese';
                else langKey = 'english';
            }
            let languageName = t(langKey);
            if (i18n.language === 'jp') {
                assessmentName = item.assessmentName_JP || item.assessmentName;
            } else if (i18n.language === 'ch') {
                assessmentName = item.assessmentName_CH || item.assessmentName;
            }
            return {
                id: item.id,
                templateMasterId: item.templateMasterId,
                langId: item.langId,
                assessmentName,
                shortCode: item.shortCode,
                runningVersion: item.runningVersion,
                uploadedDate: item.UploadedDate,
                slot: item.slot,
                language: languageName,
                isPublished: item.isPublished,
            };
        }),
        "uploadedDate"
    );

    const getTemplateList = () => {
        // Determine language from formData.language (dropdown selection)
        let lang = formData.language;
        // fallback to i18n.language if not set
        if (!lang) lang = i18n.language;
        return templates.map((template) => {
            let label = template.name;
            if ((lang === '2' || lang === 'jp' || lang === 'japanese') && template.name_jp) {
                label = template.name_jp;
            } else if ((lang === '19' || lang === 'ch' || lang === 'chinese') && template.name_ch) {
                label = template.name_ch;
            }
            return {
                label,
                value: template.id,
            };
        });
    };

    const getFieldsData = () => {
        if (modalMode === "upload") {
            return [
                {
                    fieldName: t("selectLanguage"),
                    type: "dropdown",
                    name: "language",
                    options: languageOptions,
                    required: true
                },
                {
                    fieldName: t("selectTemplateName"),
                    type: "dropdown",
                    name: "templateName",
                    options: getTemplateList(),
                    required: true
                },
                {
                    fieldName: t("uploadTemplate"),
                    type: "file",
                    name: "assessmentFile",
                    showUploadIcon: true,
                    required: true
                },
            ];
        } else if (modalMode === "edit") {
            return [
                {
                    fieldName: t("templateName"),
                    type: "input",
                    name: "templateName",
                },
                {
                    fieldName: t("level"),
                    type: "dropdown",
                    name: "level",
                    options: [
                        { label: t("easy"), value: "basic" },
                        { label: t("medium"), value: "medium" },
                        { label: t("hard"), value: "hard" },
                    ],
                },
                {
                    fieldName: t("questionType"),
                    type: "dropdown",
                    name: "questionType",
                    options: [
                        { label: t("multipleChoice"), value: "mcq" },
                        { label: t("trueOrFalse"), value: "true_false" },
                        { label: t("yesNo"), value: "yes_no" },
                    ],
                },
                { fieldName: t("question"), type: "textarea", name: "question" },
                { fieldName: t("option1"), type: "input", name: "option1" },
                { fieldName: t("option2"), type: "input", name: "option2" },
                {
                    fieldName: t("scoringOption"),
                    type: "dropdown",
                    name: "scoringOption",
                    options: [
                        { label: t("auto"), value: "auto" },
                        { label: t("manual"), value: "manual" },
                    ],
                },
                { fieldName: t("evidence"), type: "input", name: "evidence" },
                {
                    fieldName: t("hitachiGuidance"),
                    type: "input",
                    name: "guidance",
                },
            ];
        } else if (modalMode === "create") {
            return [
                 {
                    fieldName: t("selectLanguage"),
                    type: "dropdown",
                    name: "language",
                    options: languageOptions,
                    required: true
                },
            {
                    fieldName: t("templateName"),
                    type: "dropdown",
                    name: "templateName",
                    options: getTemplateList(),
                    required: true
                },
            ];
        } else if (modalMode === "publish") {
            return [
                {
                    fieldName: t("selectSlot"),
                    type: "dropdown",
                    name: "slot",
                    options: [
                        { label: t("slot1"), value: "slot1" },
                        { label: t("slot2"), value: "slot2" },
                    ],
                    required: true
                },
                {
                    fieldName: t("selectVersion"),
                    type: "dropdown",
                    name: "version",
                    options: [
                        { label: t("version1"), value: "v1" },
                        { label: t("version2"), value: "v2" },
                    ],
                    required: true
                },
            ];
        } else if (modalMode === "download" || isDownloadModalOpen) {
            return [
                {
                    fieldName: t("selectLanguage"),
                    type: "dropdown",
                    name: "language",
                    options: languageOptions,
                },
                {
                    fieldName: t("templateName"),
                    type: "dropdown",
                    name: "templateName",
                    options: getTemplateList(),
                },
            ];
        }
        return [];
    };

    // Open Modal & Reset Form Data
    const openModal = (row, rowIndex, mode) => {
        setModalMode(mode || "edit");
        if (mode === "upload") {
            setFormData({
                templateName: templates[0]?.id || "",
                language: "1",
                assessmentFile: null,
            });
        } else if (mode === "view") {
            setActiveTab("Tab2");
            setTableRowData(row);
            const assessmentId = row.templateMasterId;
            const langId = row.langId;
            fetchAssessmentDetails(assessmentId, langId);
        } else if (mode === "create") {
            setFormData({
                templateName: templates[0]?.id || "",
                language: "1",
            });
        } else {
            setFormData({});
        }
        setModalOpen(true);
    };

    // Handle form input changes
    const handleInputChange = (target, field) => {
        if (target.type === "file") {
            const validTypes = [
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ];
            if (!validTypes.includes(target?.files[0]?.type)) {
                toast.error(t("invalidFileType"), {
                    position: "top-center",
                });
                return;
            } else {
                setFormData((prev) => ({
                    ...prev,
                    [target.name]: target.files[0],
                }));
                return;
            }
        } else {
            setFormData((prev) => ({
                ...prev,
                [target.name]: target.value,
            }));
        }
    };

    // Handle "Save" button click in Upload Modal (opens Publish Modal)
    const onSaveUpload = async () => {
        // setModalMode("publish");
        setLoading(true);
        const formUploadData = new FormData();

        if (!formData.assessmentFile) {
            toast.error(t("uploadTemplateFile"), {
                position: "top-center",
                theme: "dark",
            });
            setLoading(false);
            return;
        }

        formUploadData.append("TemplateMasterId", formData.templateName);
        formUploadData.append("LanguageId", formData.language);
        formUploadData.append("File", formData.assessmentFile);
        try {
            const result = await saveAssessment(formUploadData);
            if (result !== null) {
                setModalOpen(false);
            }
            fetchAssessmentData();
        } catch (error) {
            console.error("Error fetching data:", error);
            setModalOpen(false);
        } finally {
            setLoading(false);
            // setModalOpen(false);
        }
    };

    const onCreate = async () => {
        let templateId = formData.templateName;
        if (typeof formData.templateName !== "number")
            templateId = parseInt(formData.templateName);
        let selectedTemplateName = templates.filter(
            (item) => item.id === templateId
        );

        const createData = {
            templateMasterId: formData.templateName,
            languageId: formData.language,
            selectedTempName: selectedTemplateName[0].name,
        };
        try {
            setAssessmentPayload(createData);
            setActiveTab("Tab2");
            setIsAddQueModalOpen(true);

            // setLoading(true);
            // const result = await createAssessment(createData);
            // await fetchAssessmentDetails(
            //     formData.templateName,
            //     formData.language
            // );
        } catch (error) {
            console.error("Error creating assessment:", error);
        } finally {
            setLoading(false);
            setModalOpen(false);
            // setActiveTab("Tab2");
        }
    };

    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);

    const handleDownloadSampleTemplate = () => {
        setFormData({
            language: languageOptions[0]?.value || '',
            templateName: templates[0]?.id || '',
        });
        setModalMode("download");
        setIsDownloadModalOpen(true);
    };

    const handleTemplateDownload = async () => {
        try {
            // Validate required fields
            const requiredFields = ['templateName', 'language'];
            const missingFields = requiredFields.filter(field => !formData[field]);
            
            if (missingFields.length > 0) {
                toast.error(`${t('pleaseProvide')} ${missingFields.map(field => t(field)).join(', ')}`);
                return;
            }

            // For now, just close the modal and show success message
            setIsDownloadModalOpen(false);

            // Call API to download template
            downloadTemplate();
        } catch (error) {
            console.error('Error downloading template:', error);
            toast.error(t('errorDownloadingTemplate'));
        }
    };    
    
    const downloadTemplate = async () => {
        try {
            setLoading(true);
            // Here you can use formData.templateName and formData.language
            const result = await downloadBlankTemplate(formData.templateName, formData.language);
            const blob = new Blob([result], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "SampleAssessmentTemplate.xlsx";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            setIsDownloadModalOpen(false);
        } catch (error) {
            console.error("Error downloading template:", error);
        } finally {
            setLoading(false);
        }
    };
    return (
        <>
            {loading ? (
                <Loader />
            ) : (
                <div className="assessmentTable-outer1">
                    {/* Table Component */}
                    <div className="glbOuterWrapper">
                        <div className="surveyHeader">
                            <h2 className="surveyTitle">{t("listOfAssessments")}</h2>
                            <div className="assessmentTable-UpperButtons">
                                <button
                                    className="assessmentTable-btn"
                                    onClick={downloadTemplate}
                                >
                                    {t("downloadAssessmentTemplate")}
                                    <img
                                        src={downloadIcon}
                                        alt="Download"
                                        style={{
                                            width: "16px",
                                            height: "18px",
                                        }}
                                    />
                                </button>
                                <button
                                    className="assessmentTable-btn"
                                    style={{ marginLeft: "10px" }}
                                    onClick={() => openModal("", "", "create")}
                                >
                                    {t("createAssessment")}
                                </button>
                                <button
                                    className="assessmentTable-btn"
                                    style={{ marginLeft: "10px" }}
                                    onClick={() => openModal("", "", "upload")}
                                >
                                    {t("uploadAssessment")}
                                </button>
                            </div>
                        </div>
                        <hr></hr>
                        <div style={{ marginTop: "1rem" }}>
                            <DataTableComponent
                                columns={columns}
                                data={data}
                                tableName="AdminAssessmentTable"
                                onEditDetails={openModal}
                                pageCount={5}
                            />
                        </div>
                    </div>
                    {/* Modal Components */}
                    {isModalOpen && (
                        <ModalComponent
                            isOpen={isModalOpen}
                            onClose={() => setModalOpen(false)}
                            modalTitle={
                                modalMode === "upload"
                                    ? t("uploadAssessmentTemplate")
                                    : modalMode === "publish"
                                        ? t("publishAssessment")
                                        : modalMode === "create"
                                            ? t("createAssessment")
                                            : t("editAssessment")
                            }
                            fieldsData={getFieldsData()}
                            formData={formData}
                            onChangeInput={handleInputChange}
                            buttons={[
                                modalMode === "edit" ? (
                                    <button
                                        className="saveButton"
                                        key="save"
                                        onClick={() => setModalOpen(false)}
                                    >
                                        {t("update")}
                                    </button>
                                ) : modalMode === "upload" ? (
                                    <button
                                        className="saveButton"
                                        key="save"
                                        onClick={onSaveUpload}
                                    >
                                        {t("upload")}
                                    </button>
                                ) : modalMode === "publish" ? (
                                    <button className="saveButton" key="publish">
                                        {t("publish")}
                                    </button>
                                ) : modalMode === "create" ? (
                                    <button className="saveButton" key="publish" onClick={onCreate}>
                                        {t("create")}
                                    </button>
                                ) : (
                                    <button className="saveButton" key="save">
                                        {t("save")}
                                    </button>
                                ),
                                <button
                                    className="cancelButton"
                                    key="cancel"
                                    onClick={() => setModalOpen(false)}
                                >
                                    {t("cancel")}
                                </button>,
                            ]}
                        />
                    )}
                    {/* Download Modal */}
                    {isDownloadModalOpen && (
                        <ModalComponent
                            isOpen={isDownloadModalOpen}
                            onClose={() => setIsDownloadModalOpen(false)}
                            modalTitle={t("downloadAssessmentTemplate")}
                            fieldsData={getFieldsData()}
                            formData={formData}
                            onChangeInput={handleInputChange}
                            buttons={[
                                <button
                                    className="saveButton"
                                    key="download"
                                    onClick={downloadTemplate}
                                >
                                    {t("download")}
                                </button>,
                                <button
                                    className="cancelButton"
                                    key="cancel"
                                    onClick={() => setIsDownloadModalOpen(false)}
                                >
                                    {t("cancel")}
                                </button>,
                            ]}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default AdminAssessmentTable;
